import { useState } from "react";
import { CalendarDays, Check, FileText, LoaderCircle, MapPinned, Radio, Search, Waypoints } from "lucide-react";
import { setRunLocality } from "../api";
import type { LiveEvent, MapRecord, ProcessingRun } from "../types";

interface ProcessingViewProps { map: MapRecord; run: ProcessingRun; logs: LiveEvent[]; connected: boolean; onRun: (run: ProcessingRun) => void }

const steps = [
  { key: "render", title: "Karte vorbereiten", text: "PDF oder Bild in hoher Auflösung einlesen", icon: FileText },
  { key: "detect", title: "Marker & Referenzen erkennen", text: "Pinke Höfe und verlässliche Kartensymbole finden", icon: Search },
  { key: "georeference", title: "Georeferenzierung", text: "Referenzen robust mit OpenStreetMap abgleichen", icon: Waypoints },
  { key: "cluster", title: "Gebiete & Laufpläne", text: "Dichte Bereiche gruppieren und Fußwege optimieren", icon: MapPinned },
  { key: "export", title: "Qualität prüfen & exportieren", text: "Inlier, Ausdehnung und Unsicherheit validieren", icon: MapPinned },
];

export function ProcessingView({ map, run, logs, connected, onRun }: ProcessingViewProps) {
  const [locality, setLocalityValue] = useState(run.locality || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const activeIndex = run.status === "completed" ? steps.length : run.status === "needs_input" ? 2 : Math.max(0, steps.findIndex((step) => step.key === run.step));

  async function submitLocality() {
    if (locality.trim().length < 2) return;
    setSending(true); setError("");
    try { onRun(await setRunLocality(run.id, locality.trim())); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Ort konnte nicht gesetzt werden"); }
    finally { setSending(false); }
  }

  return (
    <main className="processing-layout">
      <section className="processing-main">
        <span className="file-chip"><FileText size={15} /> <span>{run.filename}</span></span>
        {run.event_date && <span className="file-chip date-chip"><CalendarDays size={15} /> <span>{formatDate(run.event_date)}</span></span>}
        <div className="processing-title-row">
          <div><h1>{run.status === "needs_input" ? "Eine Angabe fehlt" : "Karte wird analysiert …"}</h1><p>{run.message}</p></div>
          <span className={`live-pill ${connected ? "connected" : "fallback"}`}><Radio size={13} /> {connected ? "LIVE" : "POLLING"}</span>
        </div>
        <div className="progress-track"><span style={{ width: `${run.progress}%` }} /></div>
        <div className="progress-meta"><span>Fortschritt</span><strong>{run.progress}%</strong></div>

        {run.status === "needs_input" && (
          <div className="input-request">
            <span className="request-icon"><MapPinned size={21} /></span>
            <div><strong>Wo liegt die Karte?</strong><p>Stadtteil und Stadt reichen, z. B. „Laim, München“.</p>
              <div className="request-row"><input value={locality} onChange={(event) => setLocalityValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void submitLocality(); }} autoFocus placeholder="Stadtteil, Stadt" /><button className="button button-primary" onClick={() => void submitLocality()} disabled={sending || locality.trim().length < 2}>{sending ? "Prüfe …" : "Weiter"}</button></div>
              {error && <span className="form-error">{error}</span>}
            </div>
          </div>
        )}

        <div className="step-list">
          {steps.map((step, index) => {
            const done = index < activeIndex || run.step === "completed";
            const active = index === activeIndex && run.status !== "needs_input";
            const Icon = step.icon;
            return <div className={`process-step ${done ? "done" : ""} ${active ? "active" : ""}`} key={step.key}>
              <div className="step-marker">{done ? <Check size={17} strokeWidth={3} /> : active ? <LoaderCircle className="spin" size={17} /> : <Icon size={16} />}</div>
              <div><h3>{step.title}{done && <span className="done-badge">erledigt</span>}</h3><p>{step.text}</p></div>
            </div>;
          })}
        </div>
      </section>
      <aside className="event-log">
        <div className="event-head"><span>Ereignis-Log</span><span className={connected ? "ws-on" : "ws-off"}><i /> {connected ? "ws verbunden" : "Fallback aktiv"}</span></div>
        <div className="log-lines" aria-live="polite">
          <div className="log-line"><time>{formatTime(run.created_at)}</time><span className="log-info">Auftrag {run.id.slice(0, 5)} gestartet</span></div>
          {logs.filter((log) => ["run.log", "run.needs_input", "run.failed"].includes(log.type)).map((log, index) => (
            <div className="log-line" key={`${log.timestamp}-${index}`}><time>{formatTime(log.timestamp)}</time><span className={log.type === "run.failed" || log.type === "run.needs_input" ? "log-warn" : "log-ok"}>{log.message}</span></div>
          ))}
          {run.status === "processing" && <div className="log-line"><time>{formatTime()}</time><span className="cursor">▍</span></div>}
        </div>
        <div className="log-note"><span>{map.kind === "custom" ? "Eigener Import" : "Event-Karte"}</span>{map.kind === "custom" ? "Sichere dir nach der Verarbeitung den persönlichen Link. Er bleibt durch jeden Aufruf weitere 30 Tage aktiv." : "Nach erfolgreicher Verarbeitung wird diese Karte dauerhaft mit dem Termin verknüpft."}</div>
      </aside>
    </main>
  );
}

function formatTime(value?: string) {
  const date = value ? new Date(value) : new Date();
  return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(date);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("de-DE", { weekday: "short", day: "2-digit", month: "long", year: "numeric" }).format(new Date(`${value}T12:00:00`));
}
